import type { AnalysisMode, Currency, Platform } from '@rfz/contracts';
import { Badge, Button, Input, cn } from '@rfz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import {
  ArrowRight,
  BarChart3,
  Bell,
  BookOpen,
  ChevronDown,
  CircleDollarSign,
  CircleUserRound,
  Clock3,
  Database,
  ListChecks,
  Loader2,
  LogOut,
  Moon,
  Pause,
  Play,
  RefreshCw,
  Settings2,
  Sparkles,
  Sun,
  Trash2,
  Truck,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  AUTH_REQUIRED_EVENT,
  controlJob,
  checkReleases,
  createBackup,
  createCostProfile,
  createExchangeRate,
  createDictionaryEntry,
  createUser,
  createTestSnapshot,
  getOperationsHealth,
  getCurrentUser,
  listJobs,
  listAiRuns,
  listBlacklist,
  listBackups,
  listCostProfiles,
  listExchangeRates,
  listImportBatches,
  listNotifications,
  listReleases,
  listRestores,
  listTestSnapshots,
  listPrompts,
  listUsers,
  logout,
  resetUserPassword,
  releaseBlacklist,
  registerReleaseSimulation,
  restoreBackup,
  runOperationsCleanup,
  searchDictionary,
  updateUser,
  simulateReleaseInstall,
  undoImportBatch,
  acknowledgeNotification,
  type CurrentUser,
  type JobSummary,
} from './api.js';
import { LoginScreen } from './LoginScreen.js';
import { ProductResearchFlow, RecentAnalysesPanel } from './ProductResearchFlow.js';
import {
  OPEN_SEO_PROMPT_EVENT,
  SeoPromptDialog,
  type SeoPromptOpenDetail,
} from './SeoPromptDialog.js';
import { WbSelectionWorkbench } from './WbSelectionWorkbench.js';
import homeSidebarWash from './assets/home-sidebar-wash-clean.png';

type ModuleId = 'analysis' | Platform;
type RouteId = ModuleId | 'seo-prompt';

interface AnalysisLocation {
  mode: AnalysisMode;
  taskId: string | null;
}

function normalizeAnalysisMode(value: string | null | undefined): AnalysisMode {
  return value === 'wb' ? value : 'standalone';
}

function readAnalysisLocation(): AnalysisLocation {
  const query = new URLSearchParams(window.location.search);
  return {
    mode: normalizeAnalysisMode(query.get('mode')),
    taskId: query.get('taskId'),
  };
}

function moduleFromPathname(): RouteId | null {
  if (window.location.pathname === '/analysis') return 'analysis';
  if (window.location.pathname === '/wb') return 'wb';
  if (window.location.pathname === '/seo-prompt') return 'seo-prompt';
  return null;
}

interface SeoPromptRouteState extends SeoPromptOpenDetail {
  source: 'home' | 'analysis';
  returnTo: string | null;
}

const emptySeoPromptRouteState: SeoPromptRouteState = {
  source: 'home',
  returnTo: null,
  platform: null,
  keywords: [],
  initialProductText: '',
};

function readSeoPromptRouteState(): SeoPromptRouteState {
  const value = (window.history.state as { seoPrompt?: Partial<SeoPromptRouteState> } | null)
    ?.seoPrompt;
  if (!value) return emptySeoPromptRouteState;
  const fromAnalysis =
    value.source === 'analysis' &&
    typeof value.returnTo === 'string' &&
    value.returnTo.startsWith('/analysis');
  return {
    source: fromAnalysis ? 'analysis' : 'home',
    returnTo: fromAnalysis ? value.returnTo! : null,
    platform: value.platform === 'wb' || value.platform === 'ozon' ? value.platform : null,
    keywords: Array.isArray(value.keywords) ? value.keywords : [],
    initialProductText:
      typeof value.initialProductText === 'string' ? value.initialProductText : '',
  };
}

interface DraftEnvelope<T> {
  value: T;
  touchedAt: number;
}

const DRAFT_TTL = 7 * 24 * 60 * 60 * 1000;

function useLocalDraft<T>(key: string, initial: T) {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return initial;
      const parsed = JSON.parse(raw) as DraftEnvelope<T>;
      if (Date.now() - parsed.touchedAt > DRAFT_TTL) {
        localStorage.removeItem(key);
        return initial;
      }
      return parsed.value;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    localStorage.setItem(
      key,
      JSON.stringify({ value, touchedAt: Date.now() } satisfies DraftEnvelope<T>),
    );
  }, [key, value]);
  return [value, setValue] as const;
}

const modules: Array<{ id: RouteId; label: string; subtitle: string; icon: typeof BarChart3 }> = [
  {
    id: 'analysis',
    label: '选品分析',
    subtitle: '深度分析市场与商品，发现潜在机会。',
    icon: BarChart3,
  },
  {
    id: 'wb',
    label: 'WB 选品',
    subtitle: '基于 Wildberries 数据，挖掘高潜力商品。',
    icon: Database,
  },
  {
    id: 'seo-prompt',
    label: 'SEO Prompt 生成',
    subtitle: '根据商品信息和关键词生成俄语 SEO 文案 Prompt',
    icon: Sparkles,
  },
];

function ModuleSwitcher({
  selected,
  onSelect,
}: {
  selected: RouteId | null;
  onSelect: (id: RouteId) => void;
}) {
  const reducedMotion = useReducedMotion();
  return (
    <motion.nav
      aria-label="主模块"
      className={cn(
        'module-switcher',
        selected ? 'module-switcher-docked' : 'module-switcher-home',
      )}
      initial={reducedMotion ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={reducedMotion ? {} : { opacity: 0 }}
      transition={{ duration: 0.14, ease: 'easeOut' }}
    >
      {!selected && <div className="module-workbench-heading">今日工作台</div>}
      <div className="module-list">
        {modules.map((module) => {
          const Icon = module.icon;
          return (
            <button
              type="button"
              key={module.id}
              className={cn('module-button', selected === module.id && 'module-button-active')}
              onClick={() => onSelect(module.id)}
            >
              <span className="module-icon">
                <Icon size={selected ? 17 : 24} />
              </span>
              <span>
                <strong>{module.label}</strong>
                {!selected && <small>{module.subtitle}</small>}
              </span>
              {!selected && (
                <span className="module-arrow" aria-hidden="true">
                  <ArrowRight size={20} />
                </span>
              )}
            </button>
          );
        })}
      </div>
    </motion.nav>
  );
}

function PlatformWorkbench() {
  return <WbSelectionWorkbench />;
}

function AnalysisHome({
  mode,
  taskId,
  onLocationChange,
}: AnalysisLocation & { onLocationChange: (location: AnalysisLocation) => void }) {
  return <ProductResearchFlow mode={mode} taskId={taskId} onLocationChange={onLocationChange} />;
}

type JobAction = 'pause' | 'resume' | 'cancel' | 'retry' | 'dismiss';

function JobWidget() {
  const [expanded, setExpanded] = useState(false);
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['jobs'], queryFn: listJobs, refetchInterval: 3000 });
  const jobs = query.data?.items ?? [];
  const active = jobs.filter((job) => ['queued', 'running', 'cancelling'].includes(job.status));
  // Cancelled jobs remain in the database audit trail, but cancellation also
  // means the user has dismissed them from the live task centre.
  const history = jobs.filter((job) => job.status === 'failed');
  const visibleJobs = [...active, ...history].slice(0, 6);
  const mutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: JobAction }) => controlJob(id, action),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['jobs'] }),
  });
  return (
    <aside className={cn('job-widget', expanded && 'job-widget-expanded')} aria-label="任务浮窗">
      <button type="button" className="job-widget-summary" onClick={() => setExpanded(!expanded)}>
        <span className="job-pulse">
          <ListChecks size={17} />
        </span>
        <span className="job-widget-summary-copy">
          <strong>{active.length ? `${active.length} 个任务运行中` : '任务中心'}</strong>
          <small>{active.length ? '后台任务正在处理' : '点击查看任务记录'}</small>
        </span>
        <ChevronDown className="job-widget-chevron" size={17} />
      </button>
      {expanded && (
        <div className="job-list">
          {visibleJobs.length === 0 ? (
            <p>暂无运行任务</p>
          ) : (
            visibleJobs.map((job) => (
              <JobRow
                key={job.id}
                job={job}
                onAction={(action) => mutation.mutate({ id: job.id, action })}
              />
            ))
          )}
        </div>
      )}
    </aside>
  );
}

function JobRow({ job, onAction }: { job: JobSummary; onAction: (action: JobAction) => void }) {
  const displayValue = (value: unknown, fallback: string) =>
    value == null
      ? fallback
      : typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
        ? String(value)
        : fallback;
  const jobTypeLabels: Record<string, string> = {
    'topvisor-wordstat': '查询 Yandex 搜索频次',
    'dictionary-review': '审核自动发现词',
    'competitor-fetch': '更新竞品数据',
    'external-data-query': '补充市场数据',
    'ai-keyword-review': '完善关键词信息',
    'wb-selection-retry': '重试 WB 选品处理',
    'wb-selection-depth': 'WB 选品深度分析',
    'recalculate-score': '更新选品评分',
    'rerun-risk': '复核商品风险',
    'refresh-dictionary': '更新关联词库',
    'import-spreadsheet': '导入平台数据',
    wb_import_and_calculate: '导入 WB Excel',
    'export-keywords': '导出关键词',
  };
  const depthSummary = job.jobType === 'wb-selection-depth' ? job.resultSummary : undefined;
  const total = Number(
    depthSummary?.clustered ?? job.succeededCount + job.failedCount + job.remainingCount,
  );
  const processed = job.succeededCount + job.failedCount;
  const estimatedRemaining =
    processed > 0 && job.remainingCount > 0
      ? `约 ${Math.ceil(job.remainingCount / Math.max(processed, 1)) * processed} 条`
      : `${job.remainingCount} 条`;
  const failureDistribution = [
    ...(((job.errorSummary?.failureDistribution ?? job.resultSummary?.failureDistribution) as
      | Array<{
          code?: string;
          count?: number;
        }>
      | undefined) ?? []),
  ];
  const wbImportStage =
    job.jobType === 'wb_import_and_calculate' && job.status === 'running'
      ? job.progress < 0.7
        ? `正在读取 Excel 并分块导入（${Math.round(job.progress * 100)}%）`
        : `正在计算并发布新批次（${Math.round(job.progress * 100)}%）`
      : undefined;
  const stage = displayValue(
    job.errorSummary?.stage ??
      job.resultSummary?.action ??
      job.resultSummary?.currentStage ??
      job.resultSummary?.stage ??
      job.scope?.stage ??
      wbImportStage,
    job.status === 'queued' ? '等待 Worker 接收' : '准备处理',
  );
  const exportFailures = () => {
    const failures = job.errorSummary?.failures ?? failureDistribution;
    const blob = new Blob(
      [JSON.stringify({ jobId: job.id, errorSummary: job.errorSummary, failures }, null, 2)],
      {
        type: 'application/json',
      },
    );
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `job-failures-${job.id}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className={cn('job-row', `job-row-${job.status}`)}>
      <div className="job-row-title">
        <strong>{jobTypeLabels[job.jobType] ?? '后台处理任务'}</strong>
        <span className="job-row-badges">
          <Badge>{job.platform?.toUpperCase() ?? '系统'}</Badge>
          <em className="job-state-label">
            {job.status === 'failed'
              ? '失败'
              : job.status === 'cancelled'
                ? '已取消'
                : job.status === 'running'
                  ? '运行中'
                  : job.status === 'queued'
                    ? '排队中'
                    : '已暂停'}
          </em>
        </span>
      </div>
      <div className="progress-track">
        <i style={{ width: `${Math.round(job.progress * 100)}%` }} />
      </div>
      <div className="job-meta">
        <span>
          总数 {total} · 成功 {job.succeededCount} · 失败 {job.failedCount} · 处理中{' '}
          {job.status === 'running' ? 1 : 0} · 跳过{' '}
          {Number(job.resultSummary?.skipped ?? job.resultSummary?.deferred ?? 0)}
        </span>
        <span>
          {job.status === 'running' && (
            <button type="button" aria-label="暂停" onClick={() => onAction('pause')}>
              <Pause size={14} />
            </button>
          )}
          {job.status === 'paused' && (
            <button type="button" aria-label="继续" onClick={() => onAction('resume')}>
              <Play size={14} />
            </button>
          )}
          {job.status === 'failed' && (
            <>
              <button type="button" aria-label="重试" onClick={() => onAction('retry')}>
                <RefreshCw size={14} />
              </button>
              <button
                type="button"
                className="job-dismiss-button"
                aria-label="删除这条失败任务"
                title="从任务中心删除"
                onClick={() => onAction('dismiss')}
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
          {['queued', 'running', 'paused'].includes(job.status) && (
            <button
              type="button"
              aria-label="停止并取消后台任务"
              title="停止并取消后台任务"
              onClick={() => onAction('cancel')}
            >
              <X size={14} />
            </button>
          )}
        </span>
      </div>
      <div className="job-stage-line">
        <span>当前阶段：{stage}</span>
        <span>预计剩余：{estimatedRemaining}</span>
      </div>
      {depthSummary && (
        <div className="job-stage-line">
          <span>
            已聚类 {Number(depthSummary.clustered ?? 0)} · AI 已完成{' '}
            {Number(depthSummary.aiCompleted ?? 0)} · Topvisor 已完成{' '}
            {Number(depthSummary.topvisorCompleted ?? 0)}
          </span>
          <span>
            成功 {Number(depthSummary.succeeded ?? 0)} · 暂缓 {Number(depthSummary.deferred ?? 0)} ·
            拒绝 {Number(depthSummary.rejected ?? 0)} · 失败 {Number(depthSummary.failed ?? 0)} ·
            剩余 {Number(depthSummary.remaining ?? 0)}
          </span>
        </div>
      )}
      {failureDistribution.length > 0 && (
        <div className="job-failure-distribution">
          {failureDistribution.slice(0, 4).map((item) => (
            <span key={item.code}>
              {item.code ?? 'UNKNOWN'} · {item.count ?? 0}
            </span>
          ))}
          <button type="button" onClick={exportFailures}>
            导出失败记录
          </button>
        </div>
      )}
      {job.errorSummary && !failureDistribution.length && (
        <p className="job-error-summary">
          {displayValue(job.errorSummary.code, 'PROCESSING_FAILED')}：
          {displayValue(job.errorSummary.message, '查看任务处理日志')}
        </p>
      )}
    </div>
  );
}

function AdminPanel({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['admin-users'], queryFn: listUsers });
  const batchesQuery = useQuery({ queryKey: ['admin-batches'], queryFn: listImportBatches });
  const blacklistQuery = useQuery({ queryKey: ['admin-blacklist'], queryFn: listBlacklist });
  const dictionaryQuery = useQuery({
    queryKey: ['admin-dictionary'],
    queryFn: () => searchDictionary(''),
  });
  const promptsQuery = useQuery({ queryKey: ['admin-prompts'], queryFn: listPrompts });
  const aiRunsQuery = useQuery({ queryKey: ['admin-ai-runs'], queryFn: listAiRuns });
  const notificationsQuery = useQuery({
    queryKey: ['admin-notifications'],
    queryFn: listNotifications,
  });
  const costProfilesQuery = useQuery({
    queryKey: ['admin-cost-profiles'],
    queryFn: () => listCostProfiles(),
  });
  const exchangeRatesQuery = useQuery({
    queryKey: ['admin-exchange-rates'],
    queryFn: listExchangeRates,
  });
  const operationsHealthQuery = useQuery({
    queryKey: ['admin-operations-health'],
    queryFn: getOperationsHealth,
    refetchInterval: 30_000,
  });
  const backupsQuery = useQuery({
    queryKey: ['admin-backups'],
    queryFn: listBackups,
    refetchInterval: 5_000,
  });
  const restoresQuery = useQuery({
    queryKey: ['admin-restores'],
    queryFn: listRestores,
    refetchInterval: 5_000,
  });
  const testSnapshotsQuery = useQuery({
    queryKey: ['admin-test-snapshots'],
    queryFn: listTestSnapshots,
    refetchInterval: 5_000,
  });
  const releasesQuery = useQuery({
    queryKey: ['admin-releases'],
    queryFn: listReleases,
  });
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [message, setMessage] = useState('');
  const [dictionaryKeyword, setDictionaryKeyword] = useState('');
  const [dictionaryTranslation, setDictionaryTranslation] = useState('');
  const [dictionaryProductType, setDictionaryProductType] = useState('');
  const [profilePlatform, setProfilePlatform] = useState<Platform>('wb');
  const [profileName, setProfileName] = useState('');
  const [fulfillmentMethod, setFulfillmentMethod] = useState('FBO');
  const [profileCurrency, setProfileCurrency] = useState<Currency>('RUB');
  const [commissionRate, setCommissionRate] = useState('0');
  const [serviceFeeRate, setServiceFeeRate] = useState('0');
  const [rateBase, setRateBase] = useState<Currency>('RUB');
  const [rateQuote, setRateQuote] = useState<Currency>('CNY');
  const [rateValue, setRateValue] = useState('');
  const [rateSource, setRateSource] = useState('人工结算汇率');
  const [snapshotSize, setSnapshotSize] = useState('1000');
  const [snapshotPlatforms, setSnapshotPlatforms] = useState<Platform[]>(['wb']);
  const [simulationTag, setSimulationTag] = useState('v1.0.0');
  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: async () => {
      setUsername('');
      setPassword('');
      setMessage('账号已创建');
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const updateMutation = useMutation({
    mutationFn: ({
      target,
      patch,
    }: {
      target: CurrentUser;
      patch: { role?: 'admin' | 'user'; isActive?: boolean };
    }) => updateUser(target, patch),
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['admin-users'] }),
  });
  const resetMutation = useMutation({
    mutationFn: ({ target, nextPassword }: { target: CurrentUser; nextPassword: string }) =>
      resetUserPassword(target, nextPassword),
    onSuccess: async () => {
      setMessage('密码已重置，旧会话已失效');
      await queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
  const undoMutation = useMutation({
    mutationFn: undoImportBatch,
    onSuccess: async () => {
      setMessage('批次已按字段事件回放撤销');
      await queryClient.invalidateQueries({ queryKey: ['admin-batches'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const releaseMutation = useMutation({
    mutationFn: releaseBlacklist,
    onSuccess: async () => {
      setMessage('黑名单限制已解除，后续导入可重新写入');
      await queryClient.invalidateQueries({ queryKey: ['admin-blacklist'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const dictionaryMutation = useMutation({
    mutationFn: () =>
      createDictionaryEntry({
        canonicalKeyword: dictionaryKeyword,
        chineseTranslation: dictionaryTranslation || null,
        standardProductName: dictionaryTranslation || null,
        productType: dictionaryProductType || null,
        mainKeyword: dictionaryKeyword,
        aliases: [],
        locked: true,
      }),
    onSuccess: async () => {
      setDictionaryKeyword('');
      setDictionaryTranslation('');
      setDictionaryProductType('');
      setMessage('词库条目已创建并锁定');
      await queryClient.invalidateQueries({ queryKey: ['admin-dictionary'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const costProfileMutation = useMutation({
    mutationFn: () =>
      createCostProfile({
        platform: profilePlatform,
        fulfillmentMethod,
        name: profileName,
        defaultCurrency: profileCurrency,
        config: {
          commissionRate: (Number(commissionRate) || 0) / 100,
          serviceFeeRate: (Number(serviceFeeRate) || 0) / 100,
          advertisingRate: 0,
          returnLossRate: 0,
          taxRate: 0,
          exchangeMarkupRate: 0,
          targetMarginRate: 0.25,
          fixedCosts: [],
          logistics: {
            volumeDivisor: 5_000,
            ratePerKg: null,
            firstLeg: null,
            tailLeg: null,
            handling: null,
            manualOverride: null,
          },
        },
      }),
    onSuccess: async () => {
      setProfileName('');
      setMessage('成本模板已保存；历史分析快照不会被改写');
      await queryClient.invalidateQueries({ queryKey: ['admin-cost-profiles'] });
      await queryClient.invalidateQueries({ queryKey: ['cost-profiles'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const exchangeRateMutation = useMutation({
    mutationFn: () =>
      createExchangeRate({
        baseCurrency: rateBase,
        quoteCurrency: rateQuote,
        rate: Number(rateValue),
        source: rateSource,
        rateDate: new Date().toISOString(),
        status: 'settlement',
      }),
    onSuccess: async () => {
      setRateValue('');
      setMessage('结算汇率已保存');
      await queryClient.invalidateQueries({ queryKey: ['admin-exchange-rates'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const acknowledgeMutation = useMutation({
    mutationFn: acknowledgeNotification,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['admin-notifications'] }),
  });
  const backupMutation = useMutation({
    mutationFn: () => createBackup(false),
    onSuccess: async () => {
      setMessage('完整备份已进入队列，Worker 将执行 pg_dump 和校验');
      await queryClient.invalidateQueries({ queryKey: ['admin-backups'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const restoreMutation = useMutation({
    mutationFn: restoreBackup,
    onSuccess: async () => {
      setMessage('恢复任务已进入维护模式队列；执行前会自动创建新备份');
      await queryClient.invalidateQueries({ queryKey: ['admin-restores'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const snapshotMutation = useMutation({
    mutationFn: () =>
      createTestSnapshot({ platforms: snapshotPlatforms, sampleSize: Number(snapshotSize) }),
    onSuccess: async () => {
      setMessage('脱敏快照已进入队列，测试环境现有业务样本将被覆盖');
      await queryClient.invalidateQueries({ queryKey: ['admin-test-snapshots'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const releaseCheckMutation = useMutation({
    mutationFn: checkReleases,
    onSuccess: async () => {
      setMessage('已读取最新正式 GitHub Release；没有执行安装');
      await queryClient.invalidateQueries({ queryKey: ['admin-releases'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const releaseRegisterMutation = useMutation({
    mutationFn: () =>
      registerReleaseSimulation({
        tag: simulationTag,
        releaseNotes: '阶段五本地更新编排验收，不连接 Production。',
        failSafeDigest: 'a'.repeat(64),
      }),
    onSuccess: async () => {
      setMessage('本地模拟 Release 已登记，可分别验证成功与失败回滚');
      await queryClient.invalidateQueries({ queryKey: ['admin-releases'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const releaseSimulationMutation = useMutation({
    mutationFn: simulateReleaseInstall,
    onSuccess: async (record) => {
      setMessage(
        record.status === 'rolled_back' ? '模拟失败已自动回滚并退出维护模式' : '模拟更新成功',
      );
      await queryClient.invalidateQueries({ queryKey: ['admin-releases'] });
      await queryClient.invalidateQueries({ queryKey: ['admin-operations-health'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  const cleanupMutation = useMutation({
    mutationFn: runOperationsCleanup,
    onSuccess: async () => {
      setMessage('保留策略清理已完成；关键管理日志未删除');
      await queryClient.invalidateQueries({ queryKey: ['admin-operations-health'] });
    },
    onError: (error: Error) => setMessage(error.message),
  });
  return (
    <div className="admin-overlay" role="dialog" aria-modal="true" aria-label="用户与系统管理">
      <button
        type="button"
        className="admin-backdrop"
        aria-label="关闭管理面板"
        onClick={onClose}
      />
      <aside className="admin-panel">
        <header>
          <div>
            <p className="eyebrow">ADMIN CONSOLE</p>
            <h2>用户管理</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="关闭">
            <X size={18} />
          </Button>
        </header>
        <section className="admin-create">
          <h3>创建内部账号</h3>
          <div className="admin-form-grid">
            <Input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="用户名"
              aria-label="新用户名"
            />
            <Input
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              placeholder="至少 6 位密码"
              aria-label="新用户密码"
            />
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as 'admin' | 'user')}
              aria-label="新用户角色"
            >
              <option value="user">普通用户</option>
              <option value="admin">管理员</option>
            </select>
            <Button
              disabled={createMutation.isPending || username.length < 2 || password.length < 6}
              onClick={() => createMutation.mutate({ username, password, role })}
            >
              创建账号
            </Button>
          </div>
          {message && <p className="admin-message">{message}</p>}
        </section>
        <section className="admin-users operations-console">
          <div className="admin-section-title">
            <h3>
              <Database size={17} /> 运维与发布准备
            </h3>
            <Badge>{operationsHealthQuery.data?.status ?? '检查中'}</Badge>
          </div>
          <div className="operations-health-grid">
            <div>
              <small>数据库</small>
              <strong>{operationsHealthQuery.data?.checks.database ?? '—'}</strong>
            </div>
            <div>
              <small>磁盘</small>
              <strong>
                {operationsHealthQuery.data?.diskUsedPercent == null
                  ? '—'
                  : `${operationsHealthQuery.data.diskUsedPercent.toFixed(1)}%`}
              </strong>
            </div>
            <div>
              <small>任务积压</small>
              <strong>{operationsHealthQuery.data?.queuedJobs ?? '—'}</strong>
            </div>
            <div>
              <small>最近备份</small>
              <strong>
                {operationsHealthQuery.data?.latestBackupAt
                  ? new Date(operationsHealthQuery.data.latestBackupAt).toLocaleString()
                  : '尚无'}
              </strong>
            </div>
          </div>
          <div className="operations-actions">
            <Button disabled={backupMutation.isPending} onClick={() => backupMutation.mutate()}>
              {backupMutation.isPending ? (
                <Loader2 className="spin" size={15} />
              ) : (
                <Database size={15} />
              )}
              立即完整备份
            </Button>
            <Button
              variant="secondary"
              disabled={cleanupMutation.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    '执行保留策略清理？普通日志 180 天、撤销明细 90 天，关键管理日志永久保留。',
                  )
                )
                  cleanupMutation.mutate();
              }}
            >
              执行清理策略
            </Button>
          </div>
          <div className="operations-subsection">
            <div className="admin-section-title">
              <h4>备份与恢复</h4>
              <Badge>{backupsQuery.data?.items.length ?? 0} 份记录</Badge>
            </div>
            {backupsQuery.data?.items.slice(0, 6).map((backup) => (
              <div className="admin-record-row" key={backup.id}>
                <div>
                  <strong>{backup.fileName ?? `备份 ${backup.id.slice(0, 8)}`}</strong>
                  <small>
                    {backup.triggerKind} · {backup.status} ·{' '}
                    {backup.sizeBytes
                      ? `${(backup.sizeBytes / 1024 / 1024).toFixed(1)} MB`
                      : '等待校验'}
                    {backup.protected ? ' · 已保护' : ''}
                  </small>
                </div>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={
                    backup.status !== 'succeeded' ||
                    Boolean(backup.prunedAt) ||
                    restoreMutation.isPending
                  }
                  onClick={() => {
                    const confirmation = window.prompt(
                      `危险操作：输入 RESTORE ${backup.id} 继续。恢复前系统会自动创建新备份。`,
                    );
                    if (!confirmation) return;
                    const reason = window.prompt('请输入本次恢复原因（至少 8 个字符）');
                    if (!reason) return;
                    restoreMutation.mutate({
                      id: backup.id,
                      expectedVersion: backup.version,
                      confirmation,
                      reason,
                    });
                  }}
                >
                  选择恢复
                </Button>
              </div>
            ))}
            {!backupsQuery.data?.items.length && <p className="admin-empty">尚无备份记录</p>}
            {restoresQuery.data?.items.slice(0, 3).map((restore) => (
              <p className="operations-note" key={restore.id}>
                恢复 {restore.backupId.slice(0, 8)} · {restore.status}
                {restore.failureStage ? ` · 失败阶段 ${restore.failureStage}` : ''}
              </p>
            ))}
          </div>
          <div className="operations-subsection">
            <div className="admin-section-title">
              <h4>单向脱敏测试快照</h4>
              <Badge>
                {testSnapshotsQuery.data?.configured
                  ? testSnapshotsQuery.data.targetEnvironmentId
                  : '未配置独立测试库'}
              </Badge>
            </div>
            <div className="operations-inline-form">
              {(['wb'] as Platform[]).map((platform) => (
                <label key={platform}>
                  <input
                    type="checkbox"
                    checked={snapshotPlatforms.includes(platform)}
                    onChange={(event) =>
                      setSnapshotPlatforms((current) =>
                        event.target.checked
                          ? [...new Set([...current, platform])]
                          : current.filter((item) => item !== platform),
                      )
                    }
                  />
                  {platform.toUpperCase()}
                </label>
              ))}
              <Input
                type="number"
                min="1"
                max="100000"
                value={snapshotSize}
                onChange={(event) => setSnapshotSize(event.target.value)}
                aria-label="测试快照样本数"
              />
              <Button
                size="sm"
                disabled={
                  !testSnapshotsQuery.data?.configured ||
                  !snapshotPlatforms.length ||
                  snapshotMutation.isPending
                }
                onClick={() => {
                  if (
                    window.confirm(
                      '确认覆盖独立测试环境中的现有业务样本？测试结果不会回写当前数据库。',
                    )
                  )
                    snapshotMutation.mutate();
                }}
              >
                创建脱敏快照
              </Button>
            </div>
            {testSnapshotsQuery.data?.items.slice(0, 4).map((snapshot) => (
              <p className="operations-note" key={snapshot.id}>
                {snapshot.platforms.map((item) => item.toUpperCase()).join(' + ')} ·{' '}
                {snapshot.status} · {snapshot.copiedRowCount}/{snapshot.requestedSampleSize} 行 ·
                排除 {snapshot.excludedSensitiveKinds.length} 类敏感数据
              </p>
            ))}
          </div>
          <div className="operations-subsection">
            <div className="admin-section-title">
              <h4>GitHub Release 与本地回滚模拟</h4>
              <Badge>当前 {releasesQuery.data?.currentVersion ?? '—'}</Badge>
            </div>
            <div className="operations-inline-form">
              <Button
                size="sm"
                variant="secondary"
                disabled={releaseCheckMutation.isPending}
                onClick={() => releaseCheckMutation.mutate()}
              >
                检查正式 Release
              </Button>
              <Input
                value={simulationTag}
                onChange={(event) => setSimulationTag(event.target.value)}
                aria-label="本地模拟版本"
              />
              <Button
                size="sm"
                variant="secondary"
                disabled={releaseRegisterMutation.isPending}
                onClick={() => releaseRegisterMutation.mutate()}
              >
                登记本地模拟
              </Button>
            </div>
            <p className="operations-note">
              本任务禁止 Production 部署与 GitHub Release
              创建；这里只检查正式产物并验证维护、迁移、健康检查和自动回滚编排。
            </p>
            {releasesQuery.data?.items.slice(0, 5).map((release) => (
              <div className="admin-record-row" key={release.id}>
                <div>
                  <strong>{release.tag}</strong>
                  <small>
                    {release.sourceKind} · {release.status}
                    {release.failureStage ? ` · ${release.failureStage}` : ''}
                  </small>
                </div>
                {release.sourceKind === 'simulation' && (
                  <div className="operations-row-actions">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => releaseSimulationMutation.mutate({ releaseId: release.id })}
                    >
                      模拟成功
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        releaseSimulationMutation.mutate({
                          releaseId: release.id,
                          failAt: 'health_check',
                        })
                      }
                    >
                      模拟失败回滚
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
        <section className="admin-users">
          <div className="admin-section-title">
            <h3>现有账号</h3>
            <Badge>{query.data?.items.length ?? 0} 人</Badge>
          </div>
          {query.isLoading ? (
            <div className="table-state">
              <Loader2 className="spin" />
            </div>
          ) : (
            query.data?.items.map((account) => (
              <div className="admin-user-row" key={account.id}>
                <span className="admin-avatar">{account.username.slice(0, 1).toUpperCase()}</span>
                <div>
                  <strong>{account.username}</strong>
                  <small>
                    {account.role === 'admin' ? '管理员' : '普通用户'} ·{' '}
                    {account.isActive ? '使用中' : '已停用'}
                  </small>
                </div>
                <div className="admin-user-actions">
                  <select
                    aria-label={`${account.username} 的角色`}
                    value={account.role}
                    onChange={(event) =>
                      updateMutation.mutate({
                        target: account,
                        patch: { role: event.target.value as 'admin' | 'user' },
                      })
                    }
                  >
                    <option value="user">普通用户</option>
                    <option value="admin">管理员</option>
                  </select>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() =>
                      updateMutation.mutate({
                        target: account,
                        patch: { isActive: !account.isActive },
                      })
                    }
                  >
                    {account.isActive ? '停用' : '启用'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const nextPassword = window.prompt(
                        `为 ${account.username} 设置至少 6 位的新密码`,
                      );
                      if (nextPassword && nextPassword.length >= 12)
                        resetMutation.mutate({ target: account, nextPassword });
                    }}
                  >
                    重置密码
                  </Button>
                </div>
              </div>
            ))
          )}
        </section>
        <section className="admin-create">
          <div className="admin-section-title">
            <h3>
              <Truck size={17} /> 成本模板与结算汇率
            </h3>
            <Badge>{costProfilesQuery.data?.items.length ?? 0} 个模板</Badge>
          </div>
          <p className="admin-empty">
            模板按平台和履约方式管理；修改不会覆盖历史分析中的费用快照。
          </p>
          <div className="admin-form-grid">
            <select
              value={profilePlatform}
              onChange={(event) => setProfilePlatform(event.target.value as Platform)}
              aria-label="成本模板平台"
            >
              <option value="wb">Wildberries</option>
            </select>
            <Input
              value={profileName}
              onChange={(event) => setProfileName(event.target.value)}
              placeholder="模板名称"
            />
            <Input
              value={fulfillmentMethod}
              onChange={(event) => setFulfillmentMethod(event.target.value)}
              placeholder="履约方式，如 FBO"
            />
            <select
              value={profileCurrency}
              onChange={(event) => setProfileCurrency(event.target.value as Currency)}
              aria-label="模板币种"
            >
              <option value="RUB">RUB</option>
              <option value="CNY">CNY</option>
              <option value="USD">USD</option>
            </select>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={commissionRate}
              onChange={(event) => setCommissionRate(event.target.value)}
              placeholder="佣金 %"
            />
            <Input
              type="number"
              min="0"
              step="0.01"
              value={serviceFeeRate}
              onChange={(event) => setServiceFeeRate(event.target.value)}
              placeholder="服务费 %"
            />
            <Button
              disabled={
                costProfileMutation.isPending || !profileName.trim() || !fulfillmentMethod.trim()
              }
              onClick={() => costProfileMutation.mutate()}
            >
              保存成本模板
            </Button>
          </div>
          {costProfilesQuery.data?.items.slice(0, 6).map((profile) => (
            <div className="admin-record-row" key={profile.id}>
              <div>
                <strong>{profile.name}</strong>
                <small>
                  {profile.platform.toUpperCase()} · {profile.fulfillmentMethod} ·{' '}
                  {profile.defaultCurrency}
                </small>
              </div>
              <Badge>{profile.isActive ? '启用' : '停用'}</Badge>
            </div>
          ))}
          <div className="admin-section-title admin-subsection-title">
            <h3>
              <CircleDollarSign size={17} /> 新增结算汇率
            </h3>
            <Badge>{exchangeRatesQuery.data?.items.length ?? 0} 条</Badge>
          </div>
          <div className="admin-form-grid">
            <select
              value={rateBase}
              onChange={(event) => setRateBase(event.target.value as Currency)}
              aria-label="基础币种"
            >
              <option value="RUB">RUB</option>
              <option value="USD">USD</option>
              <option value="CNY">CNY</option>
            </select>
            <select
              value={rateQuote}
              onChange={(event) => setRateQuote(event.target.value as Currency)}
              aria-label="报价币种"
            >
              <option value="CNY">CNY</option>
              <option value="RUB">RUB</option>
              <option value="USD">USD</option>
            </select>
            <Input
              type="number"
              min="0"
              step="0.000001"
              value={rateValue}
              onChange={(event) => setRateValue(event.target.value)}
              placeholder="汇率"
            />
            <Input
              value={rateSource}
              onChange={(event) => setRateSource(event.target.value)}
              placeholder="来源"
            />
            <Button
              disabled={
                exchangeRateMutation.isPending || !(Number(rateValue) > 0) || rateBase === rateQuote
              }
              onClick={() => exchangeRateMutation.mutate()}
            >
              保存结算汇率
            </Button>
          </div>
          {exchangeRatesQuery.data?.items.slice(0, 5).map((rate) => (
            <div className="admin-record-row" key={rate.id}>
              <div>
                <strong>
                  1 {rate.baseCurrency} = {rate.rate} {rate.quoteCurrency}
                </strong>
                <small>
                  {rate.source} · {new Date(rate.rateDate).toLocaleString('zh-CN')}
                </small>
              </div>
              <Badge>{rate.status}</Badge>
            </div>
          ))}
        </section>
        <section className="admin-create">
          <div className="admin-section-title">
            <h3>
              <BookOpen size={17} /> 全局俄语词库
            </h3>
            <Badge>{dictionaryQuery.data?.items.length ?? 0} 条</Badge>
          </div>
          <div className="admin-form-grid">
            <Input
              value={dictionaryKeyword}
              onChange={(event) => setDictionaryKeyword(event.target.value)}
              placeholder="俄语关键词"
              aria-label="词库俄语关键词"
            />
            <Input
              value={dictionaryTranslation}
              onChange={(event) => setDictionaryTranslation(event.target.value)}
              placeholder="中文翻译 / 标准商品名"
              aria-label="词库中文翻译"
            />
            <Input
              value={dictionaryProductType}
              onChange={(event) => setDictionaryProductType(event.target.value)}
              placeholder="商品类型"
              aria-label="词库商品类型"
            />
            <Button
              disabled={dictionaryMutation.isPending || !dictionaryKeyword.trim()}
              onClick={() => dictionaryMutation.mutate()}
            >
              新增锁定词条
            </Button>
          </div>
          {dictionaryQuery.data?.items.slice(0, 8).map((record) => (
            <div className="admin-record-row" key={record.id}>
              <div>
                <strong>{record.canonicalKeyword}</strong>
                <small>
                  {record.chineseTranslation ?? '暂无翻译'} · {record.productType ?? '类型未定'} ·{' '}
                  {record.sourceKind} {record.locked ? '· 已锁定' : ''}
                </small>
              </div>
              <Badge>
                {record.confidence == null ? '人工' : `${Math.round(record.confidence * 100)}%`}
              </Badge>
            </div>
          ))}
        </section>
        <section className="admin-users">
          <div className="admin-section-title">
            <h3>Prompt 与 AI 技术记录</h3>
            <Badge>{aiRunsQuery.data?.items.length ?? 0} 次</Badge>
          </div>
          {promptsQuery.data?.items.map((prompt) => {
            const active = prompt.versions.find((version) => version.isActive);
            return (
              <div className="admin-record-row" key={prompt.id}>
                <div>
                  <strong>{prompt.name}</strong>
                  <small>
                    {prompt.taskType} · 当前 v{active?.versionNumber ?? '—'} · 共{' '}
                    {prompt.versions.length} 个版本
                  </small>
                </div>
                <Badge>结构化 schema</Badge>
              </div>
            );
          })}
          {aiRunsQuery.data?.items.slice(0, 8).map((run) => (
            <div className="admin-record-row" key={String(run.id)}>
              <div>
                <strong>{String(run.taskType)}</strong>
                <small>
                  {String(run.provider)} / {String(run.model)} · {String(run.status)} ·{' '}
                  {String(run.priorityKind)}
                </small>
              </div>
              <Badge>
                {run.confidence == null ? '—' : `${Math.round(Number(run.confidence) * 100)}%`}
              </Badge>
            </div>
          ))}
        </section>
        <section className="admin-users">
          <div className="admin-section-title">
            <h3>
              <Bell size={17} /> 管理员提醒
            </h3>
            <Badge>
              {notificationsQuery.data?.items.filter((item) => !item.acknowledgedAt).length ?? 0}{' '}
              未确认
            </Badge>
          </div>
          {notificationsQuery.data?.items.slice(0, 12).map((notice) => (
            <div className="admin-record-row" key={notice.id}>
              <div>
                <strong>{notice.title}</strong>
                <small>{notice.summary}</small>
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={Boolean(notice.acknowledgedAt) || acknowledgeMutation.isPending}
                onClick={() => acknowledgeMutation.mutate(notice.id)}
              >
                {notice.acknowledgedAt ? '已确认' : '确认'}
              </Button>
            </div>
          ))}
          {!notificationsQuery.data?.items.length && <p className="admin-empty">暂无异常提醒</p>}
        </section>
        <section className="admin-users">
          <div className="admin-section-title">
            <h3>最近导入批次</h3>
            <Badge>{batchesQuery.data?.items.length ?? 0} 批</Badge>
          </div>
          {batchesQuery.data?.items.length ? (
            batchesQuery.data.items.slice(0, 20).map((batch) => (
              <div className="admin-record-row" key={batch.id}>
                <div>
                  <strong>{batch.originalFileName}</strong>
                  <small>
                    {batch.platform.toUpperCase()} · {batch.status} · 新增 {batch.addedCount} / 更新{' '}
                    {batch.updatedCount} / 异常 {batch.issueCount} / 黑名单跳过{' '}
                    {batch.blacklistSkippedCount}
                  </small>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={
                    Boolean(batch.undoneAt) ||
                    new Date(batch.undoExpiresAt) <= new Date() ||
                    undoMutation.isPending
                  }
                  onClick={() => {
                    if (
                      window.confirm(
                        `确认撤销导入批次“${batch.originalFileName}”？系统会回放字段变更并保留后续批次的新值。`,
                      )
                    )
                      undoMutation.mutate(batch.id);
                  }}
                >
                  {batch.undoneAt ? '已撤销' : '撤销批次'}
                </Button>
              </div>
            ))
          ) : (
            <p className="admin-empty">暂无导入批次</p>
          )}
        </section>
        <section className="admin-users">
          <div className="admin-section-title">
            <h3>永久删除黑名单</h3>
            <Badge>
              {blacklistQuery.data?.items.filter((item) => !item.releasedAt).length ?? 0} 生效
            </Badge>
          </div>
          {blacklistQuery.data?.items.length ? (
            blacklistQuery.data.items.map((record) => (
              <div className="admin-record-row" key={record.id}>
                <div>
                  <strong>{record.normalizedKeyword}</strong>
                  <small>
                    {record.scope === 'global' ? '全局' : record.platform?.toUpperCase()} ·{' '}
                    {record.reason}
                  </small>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={Boolean(record.releasedAt) || releaseMutation.isPending}
                  onClick={() => releaseMutation.mutate(record)}
                >
                  {record.releasedAt ? '已解除' : '解除'}
                </Button>
              </div>
            ))
          ) : (
            <p className="admin-empty">暂无黑名单记录</p>
          )}
        </section>
        <footer>
          <p>规则、费用、AI、备份、测试环境与系统更新会在对应管理域中保持低干扰入口。</p>
        </footer>
      </aside>
    </div>
  );
}

function TopActions({
  user,
  onLogout,
  logoutPending,
  logoutError,
  onOpenAdmin,
  onOpenRecent,
}: {
  user: CurrentUser;
  onLogout: () => void;
  logoutPending: boolean;
  logoutError: string | null;
  onOpenAdmin: () => void;
  onOpenRecent: () => void;
}) {
  const [dark, setDark] = useState(() => localStorage.getItem('rfz:theme') === 'dark');
  useEffect(() => {
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    localStorage.setItem('rfz:theme', dark ? 'dark' : 'light');
  }, [dark]);
  return (
    <div className="top-actions">
      {logoutError && <span role="alert">退出失败：{logoutError}</span>}
      <Button variant="ghost" size="icon" aria-label="切换主题" onClick={() => setDark(!dark)}>
        {dark ? <Sun size={18} /> : <Moon size={18} />}
      </Button>
      <details className="account-menu">
        <summary>
          <span>
            <CircleUserRound size={18} />
          </span>
          <b>{user.username}</b>
          <ChevronDown size={14} />
        </summary>
        <div>
          <button
            type="button"
            onClick={(event) => {
              event.currentTarget.closest('details')?.removeAttribute('open');
              onOpenRecent();
            }}
          >
            <Clock3 size={15} />
            最近分析
          </button>
          {user.role === 'admin' && (
            <>
              <p className="menu-label">管理</p>
              <button
                type="button"
                onClick={(event) => {
                  event.currentTarget.closest('details')?.removeAttribute('open');
                  onOpenAdmin();
                }}
              >
                <Settings2 size={15} />
                用户与系统管理
              </button>
            </>
          )}
          <button
            type="button"
            disabled={logoutPending}
            onClick={(event) => {
              event.currentTarget.closest('details')?.removeAttribute('open');
              onLogout();
            }}
          >
            <LogOut size={15} />
            {logoutPending ? '正在退出…' : '退出登录'}
          </button>
        </div>
      </details>
      {logoutPending && <span role="status">正在退出登录…</span>}
    </div>
  );
}

function Workspace({ user, onLoggedOut }: { user: CurrentUser; onLoggedOut: () => void }) {
  const [, setPersistedSelected] = useLocalDraft<RouteId | null>('rfz:selected-module', null);
  const initialLocation = readAnalysisLocation();
  const [selected, setSelected] = useState<RouteId | null>(() => moduleFromPathname());
  const [analysisLocation, setAnalysisLocation] = useState<AnalysisLocation>(initialLocation);
  const [seoPromptState, setSeoPromptState] = useState<SeoPromptRouteState>(() =>
    readSeoPromptRouteState(),
  );
  const [adminOpen, setAdminOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const reducedMotion = useReducedMotion();
  const logoutMutation = useMutation({ mutationFn: logout, onSuccess: onLoggedOut });

  const navigateAnalysis = (
    location: AnalysisLocation,
    historyMode: 'push' | 'replace' = 'push',
  ) => {
    const next = new URL('/analysis', window.location.origin);
    next.searchParams.set('mode', normalizeAnalysisMode(location.mode));
    if (location.taskId) next.searchParams.set('taskId', location.taskId);
    window.history[historyMode === 'replace' ? 'replaceState' : 'pushState'](
      {},
      '',
      `${next.pathname}${next.search}`,
    );
    setAnalysisLocation({ mode: normalizeAnalysisMode(location.mode), taskId: location.taskId });
    setSelected('analysis');
    setPersistedSelected('analysis');
  };

  const navigateSeoPrompt = (
    detail: SeoPromptOpenDetail,
    source: SeoPromptRouteState['source'],
  ) => {
    const nextState: SeoPromptRouteState = {
      ...detail,
      source,
      returnTo:
        source === 'analysis' ? `${window.location.pathname}${window.location.search}` : null,
    };
    window.history.pushState({ seoPrompt: nextState }, '', '/seo-prompt');
    setSeoPromptState(nextState);
    setSelected('seo-prompt');
    setPersistedSelected('seo-prompt');
  };

  const navigateModule = (module: RouteId) => {
    if (module === 'analysis') {
      navigateAnalysis({ mode: 'standalone', taskId: null });
      return;
    }
    if (module === 'seo-prompt') {
      navigateSeoPrompt(
        emptySeoPromptRouteState,
        window.location.pathname === '/analysis' ? 'analysis' : 'home',
      );
      return;
    }
    window.history.pushState({}, '', `/${module}`);
    setSelected(module);
    setPersistedSelected(module);
  };

  const returnFromSeoPrompt = () => {
    const destination =
      seoPromptState.source === 'analysis' && seoPromptState.returnTo
        ? seoPromptState.returnTo
        : '/';
    window.history.replaceState({}, '', destination);
    const module = moduleFromPathname();
    if (module === 'analysis') setAnalysisLocation(readAnalysisLocation());
    setSelected(module);
    setPersistedSelected(module);
  };

  useEffect(() => {
    if (window.location.pathname === '/analysis') {
      const location = readAnalysisLocation();
      const normalized = new URL('/analysis', window.location.origin);
      normalized.searchParams.set('mode', location.mode);
      if (location.taskId) normalized.searchParams.set('taskId', location.taskId);
      window.history.replaceState({}, '', `${normalized.pathname}${normalized.search}`);
    }
    const openAnalysis = (event: Event) => {
      const detail = (event as CustomEvent<Partial<AnalysisLocation>>).detail;
      navigateAnalysis(
        { mode: normalizeAnalysisMode(detail?.mode), taskId: detail?.taskId ?? null },
        'push',
      );
    };
    const openSeoPrompt = (event: Event) => {
      const detail = (event as CustomEvent<SeoPromptOpenDetail>).detail;
      navigateSeoPrompt(
        {
          platform: detail?.platform ?? null,
          keywords: Array.isArray(detail?.keywords) ? detail.keywords : [],
          initialProductText:
            typeof detail?.initialProductText === 'string' ? detail.initialProductText : '',
        },
        window.location.pathname === '/analysis' ? 'analysis' : 'home',
      );
    };
    const restoreLocation = () => {
      const module = moduleFromPathname();
      if (module === 'analysis') {
        setAnalysisLocation(readAnalysisLocation());
        setSelected('analysis');
        setPersistedSelected('analysis');
      } else if (module === 'seo-prompt') {
        setSeoPromptState(readSeoPromptRouteState());
        setSelected('seo-prompt');
        setPersistedSelected('seo-prompt');
      } else {
        setSelected(module);
        setPersistedSelected(module);
      }
    };
    window.addEventListener('rfz:open-analysis', openAnalysis);
    window.addEventListener(OPEN_SEO_PROMPT_EVENT, openSeoPrompt);
    window.addEventListener('popstate', restoreLocation);
    return () => {
      window.removeEventListener('rfz:open-analysis', openAnalysis);
      window.removeEventListener(OPEN_SEO_PROMPT_EVENT, openSeoPrompt);
      window.removeEventListener('popstate', restoreLocation);
    };
    // Navigation helpers intentionally initialize once; browser events keep later state in sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <main
      className={cn('app-shell', selected && 'app-shell-active', !selected && 'app-shell-home')}
    >
      {!selected && (
        <img className="home-sidebar-wash" src={homeSidebarWash} alt="" aria-hidden="true" />
      )}
      <header className="topbar">
        <div className="brand-mark">
          <span>瑞</span>
          <strong>瑞峰选品</strong>
        </div>
        {selected && (
          <TopActions
            user={user}
            onLogout={() => logoutMutation.mutate()}
            logoutPending={logoutMutation.isPending}
            logoutError={logoutMutation.error?.message ?? null}
            onOpenAdmin={() => setAdminOpen(true)}
            onOpenRecent={() => setRecentOpen(true)}
          />
        )}
      </header>
      <AnimatePresence initial={false}>
        <ModuleSwitcher
          key={selected ? 'module-switcher-docked' : 'module-switcher-home'}
          selected={selected}
          onSelect={navigateModule}
        />
      </AnimatePresence>
      {!selected && (
        <motion.div
          className="welcome-copy"
          initial={reducedMotion ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.18, ease: 'easeOut' }}
        >
          <p className="eyebrow">欢迎回来</p>
          <h1>今天，专注挖掘好产品</h1>
          <p>用数据洞察趋势，用决策创造价值。</p>
        </motion.div>
      )}
      <AnimatePresence mode="wait">
        {selected && (
          <motion.div
            key={selected}
            className="workspace-stage"
            initial={reducedMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={reducedMotion ? {} : { opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            {selected === 'analysis' ? (
              <AnalysisHome
                {...analysisLocation}
                onLocationChange={(location) => navigateAnalysis(location, 'replace')}
              />
            ) : selected === 'seo-prompt' ? (
              <SeoPromptDialog
                platform={seoPromptState.platform}
                keywords={seoPromptState.keywords}
                initialProductText={seoPromptState.initialProductText}
                onClose={returnFromSeoPrompt}
                variant="page"
              />
            ) : (
              <PlatformWorkbench />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {selected && <JobWidget />}
      {recentOpen && (
        <RecentAnalysesPanel
          onClose={() => setRecentOpen(false)}
          onOpenAnalysis={(location) => {
            setRecentOpen(false);
            navigateAnalysis(location);
          }}
        />
      )}
      {adminOpen && user.role === 'admin' && <AdminPanel onClose={() => setAdminOpen(false)} />}
    </main>
  );
}

export function App() {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [checked, setChecked] = useState(false);
  useEffect(() => {
    const handleAuthRequired = () => setUser(null);
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, []);
  useEffect(() => {
    void getCurrentUser()
      .then(setUser)
      .catch(() => undefined)
      .finally(() => setChecked(true));
  }, []);
  if (!checked)
    return (
      <main className="boot-screen">
        <span className="brand-mark-large">瑞</span>
        <Loader2 className="spin" />
      </main>
    );
  return user ? (
    <Workspace user={user} onLoggedOut={() => setUser(null)} />
  ) : (
    <LoginScreen onAuthenticated={setUser} />
  );
}
